import { FILTER_FEATURE } from "api/ApiPath";
import { BaseApiResponseModel } from "app/based/baseApiModel/baseApiResponseModel/BaseApiResponseModel";
import { ListBaseApiResponseModel } from "app/based/baseApiModel/baseApiResponseModel/ListBaseApiResponseModel";
import { BaseFilterListRequestModel } from "app/based/baseApiModel/baseFilterListModel/BaseFilterListRequestModel";
import { BaseFilterListResponseModel } from "app/based/baseApiModel/baseFilterListModel/BaseFilterListResponseModel";
import { BaseUserRequestModel } from "app/based/baseApiModel/baseUserModel/BaseUserRequestModel";
import { BaseUserResponseModel } from "app/based/baseApiModel/baseUserModel/BaseUserResponseModel";
import { FilterInjection } from "app/filters/di/FilterInjection";
import { FilterUseCase } from "app/filters/domain/useCase/FilterUseCase";
import { AirTimeRegistrationRequestModel } from "features/airTimeRegistration/data/model/AirTimeRegistrationRequestModel";
import { AirTimeRegistrationResponseModel } from "features/airTimeRegistration/data/model/AirTimeRegistrationResponseModel";
import { injectable } from "tsyringe";
import { DateFormat } from "utils/format/DateFormat";
import { paramsChecking } from "utils/helpersInTs/helpersInTs";
import { AirTimeRegistrationInjection } from "../../di/AirTimeRegistrationInjection";
import { ExportPayload } from "../entities/AirTimeRegistrationPayload";
import {
  AirTimeRegistrationQuery,
  SearchContext,
  SearchFormValues,
} from "../entities/AirTimeRegistrationQuery";
import { IAirTimeRegistrationRepository } from "../repositories/IAirTimeRegistrationRepository";

@injectable()
export class AirTimeRegistrationUseCase {
  private airTimeRegistrationRepo: IAirTimeRegistrationRepository;
  private filterUseCase: FilterUseCase;

  constructor() {
    this.airTimeRegistrationRepo =
      AirTimeRegistrationInjection.getAirTimeRegistrationRepo();
    this.filterUseCase = FilterInjection.getUseCase();
  }

  private prepareFormValues(
    rawFormValues: Record<string, unknown>,
  ): SearchFormValues {
    return {
      status: paramsChecking(rawFormValues?.status, "select") as
        | number
        | string,
      code: paramsChecking(rawFormValues?.code, "input") as string,
      transRef: paramsChecking(rawFormValues?.transRef, "input") as string,
      supplierRefCode: paramsChecking(
        rawFormValues?.supplierRefCode,
        "input",
      ) as string,
      carrierCodes: paramsChecking(
        rawFormValues?.carrierCodes,
        "select",
      ) as string[],
      phoneNumber: paramsChecking(
        rawFormValues?.phoneNumber,
        "input",
      ) as string,
      partnerCode: paramsChecking(
        rawFormValues?.partnerCode,
        "select",
      ) as string,
      codeAirTime: paramsChecking(
        rawFormValues?.codeAirTime,
        "select",
      ) as string,
      userRef: (rawFormValues?.userRef as string[]) || undefined,
    };
  }

  private getDateRange(rawFormValues: Record<string, unknown>): {
    dateFrom: string;
    dateTo: string;
  } {
    const dateFrom =
      DateFormat.dateRendering(
        rawFormValues?.created as Array<Record<string, unknown>>,
        "from",
      ) ?? "";
    const dateTo =
      DateFormat.dateRendering(
        rawFormValues?.created as Array<Record<string, unknown>>,
        "to",
      ) ?? "";
    return { dateFrom, dateTo };
  }

  async getList(
    params: AirTimeRegistrationRequestModel,
  ): Promise<ListBaseApiResponseModel<AirTimeRegistrationResponseModel>> {
    return await this.airTimeRegistrationRepo.getList(params);
  }

  async getListFromSearch(
    rawFormValues: Record<string, unknown>,
    context: SearchContext,
  ): Promise<{
    list: ListBaseApiResponseModel<AirTimeRegistrationResponseModel>;
    params: AirTimeRegistrationRequestModel;
  }> {
    const formValues = this.prepareFormValues(rawFormValues);
    const { dateFrom, dateTo } = this.getDateRange(rawFormValues);
    const params = AirTimeRegistrationQuery.buildSearchQuery(
      formValues,
      context,
      dateFrom,
      dateTo,
    );
    const list = await this.airTimeRegistrationRepo.getList(params);
    return { list, params };
  }

  async getListWithTableChange(
    prevParams: AirTimeRegistrationRequestModel,
    rawFormValues: Record<string, unknown>,
    page: number,
    limit: number,
  ): Promise<{
    list: ListBaseApiResponseModel<AirTimeRegistrationResponseModel>;
    params: AirTimeRegistrationRequestModel;
  }> {
    const formValues = this.prepareFormValues(rawFormValues);
    const params = AirTimeRegistrationQuery.buildTableChangeQuery(
      prevParams,
      formValues,
      page,
      limit,
    );
    const list = await this.airTimeRegistrationRepo.getList(params);
    return { list, params };
  }

  async getListRedo(): Promise<{
    list: ListBaseApiResponseModel<AirTimeRegistrationResponseModel>;
    params: AirTimeRegistrationRequestModel;
  }> {
    const { fromDate, toDate } = DateFormat.getDateRange(0);
    const params = AirTimeRegistrationQuery.buildRedoQuery(
      fromDate ?? "",
      toDate ?? "",
    );
    const list = await this.airTimeRegistrationRepo.getList(params);
    return { list, params };
  }

  async getDetail(
    params: AirTimeRegistrationRequestModel,
  ): Promise<BaseApiResponseModel<AirTimeRegistrationResponseModel>> {
    return await this.airTimeRegistrationRepo.getDetail(params);
  }

  async exportReport(
    params: AirTimeRegistrationRequestModel,
  ): Promise<BaseApiResponseModel<Blob>> {
    return await this.airTimeRegistrationRepo.exportReport(params);
  }

  async exportReportFromSearch(
    payload: ExportPayload,
  ): Promise<BaseApiResponseModel<Blob>> {
    const formValues = this.prepareFormValues(payload.rawFormValues);
    const { dateFrom, dateTo } = this.getDateRange(payload.rawFormValues);
    const params = AirTimeRegistrationQuery.buildExportQuery(
      formValues,
      payload.context,
      dateFrom,
      dateTo,
    );
    return await this.airTimeRegistrationRepo.exportReport(params);
  }

  async getFilterList(
    params: BaseFilterListRequestModel,
    target: "carrier" | "partner" | "store" | "denomination",
  ): Promise<Array<BaseFilterListResponseModel>> {
    let path = "";
    switch (target) {
      case "carrier":
        path = FILTER_FEATURE.CARRIER_LIST;
        break;
      case "partner":
        path = FILTER_FEATURE.PARTNER_LIST;
        break;
      case "store":
        path = FILTER_FEATURE.STORE_LIST;
        break;
      case "denomination":
        path = FILTER_FEATURE.DENOMINATION_LIST;
        break;
    }
    return await this.filterUseCase.fetchListWithFeature({
      path,
      params,
      isNeedFeature: true,
    });
  }

  async getUserForFilter(
    params: BaseUserRequestModel,
    target: "staff" | "userB2B",
  ): Promise<Array<BaseUserResponseModel>> {
    let path = "";
    switch (target) {
      case "staff":
        path = FILTER_FEATURE.STAFF_LIST;
        break;
      case "userB2B":
        path = FILTER_FEATURE.USER_LIST_B2B;
        break;
    }
    return await this.filterUseCase.fetchListWithFeature({
      path,
      params,
    });
  }
}
